import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getProfilePhotoUrl } from '../utils/imageUtils';
import { 
  Container, 
  Grid, 
  Paper, 
  Typography, 
  Avatar, 
  Box, 
  Button, 
  Tabs, 
  Tab, 
  Divider, 
  IconButton,
  Chip,
  Skeleton,
  Snackbar,
  Alert
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  Edit as EditIcon,
  PhotoCamera as PhotoCameraIcon,
  Lock as LockIcon,
  Public as PublicIcon,
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import GalleryGrid from '../components/gallery/GalleryGrid';
import ProfilePhotoDialog from '../components/profile/ProfilePhotoDialog';
import EditProfileDialog from '../components/profile/EditProfileDialog';
import CreateGalleryDialog from '../components/gallery/CreateGalleryDialog';
import api from '../utils/api';

// Tab panel component for different profile sections
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ProfilePage = () => {
  const { t } = useTranslation();
  const { username } = useParams();
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [openPhotoDialog, setOpenPhotoDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openCreateGalleryDialog, setOpenCreateGalleryDialog] = useState(false);
  const [galleries, setGalleries] = useState([]);
  const [isLoadingGalleries, setIsLoadingGalleries] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const isOwnProfile = currentUser && username === currentUser.username;

  // Fetch user profile
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setIsLoading(true);
        console.log('Fetching profile for username:', username);
        console.log('API URL:', `${process.env.REACT_APP_API_URL}/users/profile/${username}`);
        const response = await api.get(`/users/profile/${username}`);
        console.log('Profile API response:', response.data);
        setProfile(response.data.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching profile:', err);
        console.error('Error details:', err.response?.data || err.message);
        setError(err.response?.data?.error || t('profile:errors.loadFailed'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, [username, t]);

  // Fetch user galleries
  useEffect(() => {
    const fetchGalleries = async () => {
      if (!profile) return;
      
      try {
        setIsLoadingGalleries(true);
        const response = await api.get('/galleries', {
          params: { userId: profile._id }
        });
        setGalleries(response.data.data);
      } catch (err) {
        console.error('Error fetching galleries:', err);
      } finally {
        setIsLoadingGalleries(false);
      }
    };

    fetchGalleries();
  }, [profile]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleUpdateProfilePhoto = (photoData) => {
    try {
      // PhotoData already contains the uploaded photo URLs from the dialog
      setProfile(prev => ({
        ...prev,
        profilePhoto: photoData.profilePhoto,
        profilePhotoThumbnail: photoData.profilePhotoThumbnail
      }));
      setOpenPhotoDialog(false);
      setSnackbar({
        open: true,
        message: t('profile:messages.photoUpdated'),
        severity: 'success'
      });
    } catch (err) {
      console.error('Error updating profile photo:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.error || t('profile:errors.photoUpdateFailed'),
        severity: 'error'
      });
    }
  };

  const handleUpdateProfile = async (profileData) => {
    try {
      const response = await api.put('/users/profile', profileData);
      
      // Update profile state with the response data
      setProfile(prev => ({
        ...prev,
        ...response.data.data
      }));
      
      setOpenEditDialog(false);
      setSnackbar({
        open: true,
        message: t('profile:messages.profileUpdated'),
        severity: 'success'
      });
    } catch (err) {
      console.error('Error updating profile:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.error || t('profile:errors.profileUpdateFailed'),
        severity: 'error'
      });
    }
  };

  const handleCreateGallery = async (galleryData) => {
    try {
      const response = await api.post('/galleries', galleryData);
      setGalleries(prev => [response.data.data, ...prev]);
      setOpenCreateGalleryDialog(false);
      setSnackbar({
        open: true,
        message: t('profile:messages.galleryCreated'),
        severity: 'success'
      });
    } catch (err) {
      console.error('Error creating gallery:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.error || t('profile:errors.galleryCreateFailed'),
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Skeleton variant="circular" width={150} height={150} />
              <Skeleton variant="text" width="60%" height={40} sx={{ mt: 2 }} />
              <Skeleton variant="text" width="40%" height={30} />
              <Skeleton variant="rectangular" width="100%" height={100} sx={{ mt: 2 }} />
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 8 }}>
            <Paper sx={{ p: 2 }}>
              <Skeleton variant="rectangular" width="100%" height={200} />
            </Paper>
          </Grid>
        </Grid>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h5" color="error" gutterBottom>
            {error}
          </Typography>
          <Typography variant="body1" sx={{ mt: 2, mb: 3 }}>
            {t('profile:errors.profileNotFound')}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button 
              variant="contained" 
              onClick={() => navigate('/dashboard')}
              sx={{ mt: 2 }}
            >
              {t('profile:actions.goToDashboard')}
            </Button>
            <Button 
              variant="outlined" 
              onClick={() => navigate(-1)}
              sx={{ mt: 2 }}
            >
              {t('profile:actions.goBack')}
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3}>
        {/* Profile sidebar */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box sx={{ position: 'relative' }}>
              <Avatar 
                src={getProfilePhotoUrl(profile.profilePhoto)} 
                alt={`${profile.firstName} ${profile.lastName}`}
                sx={{ width: 150, height: 150, mb: 2 }}
              />
              {isOwnProfile && (
                <IconButton 
                  color="primary" 
                  sx={{ 
                    position: 'absolute', 
                    bottom: 10, 
                    right: 0,
                    backgroundColor: 'white',
                    '&:hover': { backgroundColor: '#f5f5f5' }
                  }}
                  onClick={() => setOpenPhotoDialog(true)}
                >
                  <PhotoCameraIcon />
                </IconButton>
              )}
            </Box>
            
            <Typography variant="h5" gutterBottom>
              {profile.firstName} {profile.lastName}
            </Typography>
            
            <Typography variant="body2" color="text.secondary" gutterBottom>
              @{profile.username}
            </Typography>
            
            <Chip 
              icon={profile.isProfilePublic ? <PublicIcon /> : <LockIcon />}
              label={profile.isProfilePublic ? t('profile:privacy.public') : t('profile:privacy.private')}
              color={profile.isProfilePublic ? "primary" : "default"}
              size="small"
              sx={{ mb: 2 }}
            />
            
            {isOwnProfile && (
              <Button 
                variant="outlined" 
                startIcon={<EditIcon />}
                onClick={() => setOpenEditDialog(true)}
                fullWidth
                sx={{ mb: 2 }}
              >
                {t('profile:actions.editProfile')}
              </Button>
            )}
            
            <Divider sx={{ width: '100%', my: 2 }} />
            
            {profile.bio && (
              <Box sx={{ width: '100%', mb: 2 }}>
                <Typography variant="body1" paragraph>
                  {profile.bio}
                </Typography>
              </Box>
            )}
            
            {/* Social links */}
            {profile.socialLinks && Object.values(profile.socialLinks).some(link => link) && (
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                {profile.socialLinks.instagram && (
                  <IconButton 
                    color="primary" 
                    component="a" 
                    href={`https://instagram.com/${profile.socialLinks.instagram}`}
                    target="_blank"
                  >
                    <InstagramIcon />
                  </IconButton>
                )}
                {profile.socialLinks.facebook && (
                  <IconButton 
                    color="primary" 
                    component="a" 
                    href={`https://facebook.com/${profile.socialLinks.facebook}`}
                    target="_blank"
                  >
                    <FacebookIcon />
                  </IconButton>
                )}
                {profile.socialLinks.twitter && (
                  <IconButton 
                    color="primary" 
                    component="a" 
                    href={`https://twitter.com/${profile.socialLinks.twitter}`}
                    target="_blank"
                  >
                    <TwitterIcon />
                  </IconButton>
                )}
                {profile.socialLinks.linkedin && (
                  <IconButton 
                    color="primary" 
                    component="a" 
                    href={`https://linkedin.com/in/${profile.socialLinks.linkedin}`}
                    target="_blank"
                  >
                    <LinkedInIcon />
                  </IconButton>
                )}
              </Box>
            )}
          </Paper>
        </Grid>
        
        {/* Main content area */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper sx={{ width: '100%' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs 
                value={tabValue} 
                onChange={handleTabChange} 
                aria-label="profile tabs"
                centered
              >
                <Tab label={t('profile:tabs.galleries')} />
                <Tab label={t('profile:tabs.about')} />
                {/* Add more tabs as needed */}
              </Tabs>
            </Box>
            
            {/* Galleries Tab */}
            <TabPanel value={tabValue} index={0}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">
                  {t('profile:galleries.title', { count: profile.galleriesCount || 0 })}
                </Typography>
                {isOwnProfile && (
                  <Button 
                    variant="contained" 
                    startIcon={<AddIcon />}
                    onClick={() => setOpenCreateGalleryDialog(true)}
                  >
                    {t('profile:actions.createGallery')}
                  </Button>
                )}
              </Box>
              
              {isLoadingGalleries ? (
                <Grid container spacing={2}>
                  {[1, 2, 3, 4].map((item) => (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={item}>
                      <Skeleton variant="rectangular" height={200} />
                      <Skeleton variant="text" width="80%" />
                      <Skeleton variant="text" width="40%" />
                    </Grid>
                  ))}
                </Grid>
              ) : galleries.length > 0 ? (
                <GalleryGrid galleries={galleries} />
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    {isOwnProfile 
                      ? t('profile:galleries.empty.own')
                      : t('profile:galleries.empty.user')}
                  </Typography>
                  {isOwnProfile && (
                    <Button 
                      variant="contained" 
                      startIcon={<AddIcon />}
                      onClick={() => setOpenCreateGalleryDialog(true)}
                      sx={{ mt: 2 }}
                    >
                      {t('profile:actions.createFirstGallery')}
                    </Button>
                  )}
                </Box>
              )}
            </TabPanel>
            
            {/* About Tab */}
            <TabPanel value={tabValue} index={1}>
              <Typography variant="h6" gutterBottom>
                {t('profile:about.title', { name: profile.firstName })}
              </Typography>
              
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="body1">
                    {profile.bio || t('profile:about.noBio')}
                  </Typography>
                </Grid>
                
                {/* Add more profile information here */}
              </Grid>
            </TabPanel>
          </Paper>
        </Grid>
      </Grid>
      
      {/* Dialogs */}
      {isOwnProfile && (
        <>
          <ProfilePhotoDialog 
            open={openPhotoDialog}
            onClose={() => setOpenPhotoDialog(false)}
            onSave={handleUpdateProfilePhoto}
            currentPhoto={profile.profilePhoto}
          />
          
          <EditProfileDialog 
            open={openEditDialog}
            onClose={() => setOpenEditDialog(false)}
            onSave={handleUpdateProfile}
            profile={profile}
          />
          
          <CreateGalleryDialog 
            open={openCreateGalleryDialog}
            onClose={() => setOpenCreateGalleryDialog(false)}
            onSave={handleCreateGallery}
          />
        </>
      )}
      
      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ProfilePage;
